// src/modules/admin/pages/AdminStrava/index.js
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Alert, Button, Form, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { useService } from '../../../../shared/hooks/useService';
import './styles.css';

function isoDate(d) {
    return d.toISOString().slice(0, 10);
}

function daysAgo(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return isoDate(d);
}

const PRESETS = [
    { label: 'Last 7 days',   after: () => daysAgo(7),   before: () => isoDate(new Date()) },
    { label: 'Last 30 days',  after: () => daysAgo(30),  before: () => isoDate(new Date()) },
    { label: 'Last 3 months', after: () => daysAgo(90),  before: () => isoDate(new Date()) },
    { label: 'Last 6 months', after: () => daysAgo(180), before: () => isoDate(new Date()) },
];

const AdminStrava = () => {
    const service = useService();
    const { hasPermission } = useAuth();
    const hasStravaPermission = hasPermission('strava:resync');
    const hasViewAthletesPermission = hasPermission('strava:viewAthletes');

    const [users, setUsers] = useState([]);
    const [allUsers, setAllUsers] = useState(false);
    const [selectedUsername, setSelectedUsername] = useState('');
    const [dateFrom, setDateFrom] = useState(() => daysAgo(7));
    const [dateTo, setDateTo] = useState(() => isoDate(new Date()));
    const [activePreset, setActivePreset] = useState(0);
    const [overwrite, setOverwrite] = useState(false);
    const [ignorePenalty, setIgnorePenalty] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [result, setResult] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(true);

    const [athletes, setAthletes] = useState([]);
    const [loadingAthletes, setLoadingAthletes] = useState(false);
    const [athletesError, setAthletesError] = useState(null);
    const [verifying, setVerifying] = useState({});
    const [verifyResults, setVerifyResults] = useState({});

    const [quota, setQuota] = useState(null);
    const [loadingQuota, setLoadingQuota] = useState(false);
    const [quotaError, setQuotaError] = useState(null);

    const applyPreset = (idx) => {
        setActivePreset(idx);
        setDateFrom(PRESETS[idx].after());
        setDateTo(PRESETS[idx].before());
    };

    const fetchUsers = useCallback(async () => {
        try {
            const data = await service.getAllUsers({ pageSize: 100, filters: { stravaConnected: true } });
            setUsers(data.users || []);
        } catch {
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    }, [service]);

    const fetchAthletes = useCallback(async () => {
        setLoadingAthletes(true);
        setAthletesError(null);
        try {
            const data = await service.adminGetStravaAthletes();
            setAthletes(data.athletes || []);
        } catch (err) {
            setAthletesError(err.message || 'Failed to load athletes');
        } finally {
            setLoadingAthletes(false);
        }
    }, [service]);

    const fetchQuota = useCallback(async () => {
        setLoadingQuota(true);
        setQuotaError(null);
        try {
            const data = await service.adminGetStravaQuota();
            setQuota(data.quota);
        } catch (err) {
            setQuotaError(err.message || 'Failed to load quota');
        } finally {
            setLoadingQuota(false);
        }
    }, [service]);

    useEffect(() => {
        if (hasStravaPermission) fetchUsers();
    }, [hasStravaPermission, fetchUsers]);

    useEffect(() => {
        if (hasViewAthletesPermission) fetchAthletes();
    }, [hasViewAthletesPermission, fetchAthletes]);

    useEffect(() => {
        if (hasViewAthletesPermission) fetchQuota();
    }, [hasViewAthletesPermission, fetchQuota]);

    const handleVerify = async (athleteUsername) => {
        setVerifying(v => ({ ...v, [athleteUsername]: true }));
        try {
            const data = await service.adminVerifyStravaAthlete(athleteUsername);
            setVerifyResults(r => ({ ...r, [athleteUsername]: { ok: true, data } }));
        } catch (err) {
            setVerifyResults(r => ({ ...r, [athleteUsername]: { ok: false, message: err.message } }));
        } finally {
            setVerifying(v => ({ ...v, [athleteUsername]: false }));
        }
    };

    const handleResync = async (e) => {
        e.preventDefault();
        setSyncing(true);
        setResult(null);
        try {
            const username = allUsers ? '' : selectedUsername;
            const data = await service.adminResyncStrava(username, dateFrom, dateTo || null, overwrite, ignorePenalty);
            setResult({ type: 'success', data });
        } catch (err) {
            setResult({ type: 'error', message: err.message || 'Resync failed' });
        } finally {
            setSyncing(false);
        }
    };

    const canSubmit = !syncing && dateFrom && (allUsers || selectedUsername);

    if (!hasStravaPermission) {
        return (
            <Container className="admin-strava-container">
                <Alert variant="warning" className="admin-no-permission-alert">
                    <Alert.Heading>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        No Permission
                    </Alert.Heading>
                    <p>You need the <strong>strava:resync</strong> permission to use this feature.</p>
                    <Button variant="outline-warning" size="sm" as={Link} to="/admin">
                        <i className="bi bi-arrow-left me-1"></i>
                        Back to Dashboard
                    </Button>
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="admin-strava-container">
            <div className="admin-strava-card">
                <div className="admin-strava-card-header">
                    <i className="bi bi-arrow-repeat me-2"></i>
                    Resync Strava Activities
                </div>
                <div className="admin-strava-card-body">
                    <p className="admin-strava-description">
                        Imports Strava activities in the selected date range. Already-imported activities are skipped unless overwrite is enabled.
                    </p>

                    <div className="admin-strava-rate-limit-warning">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        Strava rate-limits the API heavily (100 requests / 15 min, 1000 / day). Resyncing all users or a long date range may exhaust your quota.
                    </div>

                    {process.env.REACT_APP_USE_LOCAL_AUTH === 'true' && (
                        <div className="admin-strava-local-dev-note">
                            <i className="bi bi-info-circle me-2"></i>
                            <strong>Local dev:</strong> Strava is mocked. Seed data activities have no Strava ID, so the first resync will add mock Strava activities alongside them — this is expected. Re-running the resync skips already-imported activities by Strava ID, just like production.
                        </div>
                    )}

                    <Form onSubmit={handleResync}>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="all-users-toggle"
                                label="Resync all Strava-connected users"
                                checked={allUsers}
                                onChange={e => setAllUsers(e.target.checked)}
                            />
                        </Form.Group>

                        {!allUsers && (
                            <Form.Group className="mb-3">
                                <Form.Label>User</Form.Label>
                                {loadingUsers ? (
                                    <div className="admin-strava-loading-users">
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Loading users…
                                    </div>
                                ) : users.length > 0 ? (
                                    <Form.Select
                                        value={selectedUsername}
                                        onChange={e => setSelectedUsername(e.target.value)}
                                        required={!allUsers}
                                    >
                                        <option value="">Select a user…</option>
                                        {users.map(u => (
                                            <option key={u.username} value={u.username}>
                                                {u.firstName} {u.lastName} ({u.username})
                                            </option>
                                        ))}
                                    </Form.Select>
                                ) : (
                                    <>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter username"
                                            value={selectedUsername}
                                            onChange={e => setSelectedUsername(e.target.value)}
                                            required={!allUsers}
                                        />
                                        <Form.Text className="text-muted">
                                            No Strava-connected users found via filter — enter a username manually.
                                        </Form.Text>
                                    </>
                                )}
                            </Form.Group>
                        )}

                        <Form.Group className="mb-4">
                            <Form.Label>Date range</Form.Label>
                            <div className="admin-strava-presets">
                                {PRESETS.map((p, idx) => (
                                    <button
                                        key={p.label}
                                        type="button"
                                        className={`admin-strava-preset-btn${activePreset === idx ? ' active' : ''}`}
                                        onClick={() => applyPreset(idx)}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <Flatpickr
                                value={[dateFrom, dateTo].filter(Boolean)}
                                options={{
                                    mode: 'range',
                                    dateFormat: 'Y-m-d',
                                    maxDate: 'today',
                                    allowInput: true,
                                }}
                                onChange={dates => {
                                    setActivePreset(null);
                                    setDateFrom(dates[0] ? isoDate(dates[0]) : '');
                                    setDateTo(dates[1] ? isoDate(dates[1]) : '');
                                }}
                                className="form-control"
                                placeholder="Select date range…"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <div className="d-flex align-items-center gap-2">
                                <Form.Check
                                    type="checkbox"
                                    id="overwrite-toggle"
                                    label="Overwrite existing activities"
                                    checked={overwrite}
                                    onChange={e => setOverwrite(e.target.checked)}
                                />
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="overwrite-help">
                                            <div className="text-start">
                                                <strong>Unchecked (default):</strong> only imports activities missing from FitOps — safe to run anytime to backfill gaps from webhook downtime or before the integration was set up.
                                                <hr className="my-1" />
                                                <strong>Checked:</strong> deletes and re-imports existing activities too. Reactions are preserved.
                                            </div>
                                        </Tooltip>
                                    }
                                >
                                    <i className="bi bi-info-circle admin-strava-help-icon"></i>
                                </OverlayTrigger>
                            </div>
                            {overwrite && (
                                <Form.Text className="text-warning">
                                    Existing activities in the date range will be deleted and re-imported. Reactions are preserved.
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <div className="d-flex align-items-center gap-2">
                                <Form.Check
                                    type="checkbox"
                                    id="ignore-penalty-toggle"
                                    label="Skip late-logging penalty"
                                    checked={ignorePenalty}
                                    onChange={e => setIgnorePenalty(e.target.checked)}
                                />
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="ignore-penalty-help">
                                            <div className="text-start">
                                                <strong>Checked (default):</strong> activities are imported with full points — the Strava timestamp is treated as the log date, so no late-logging penalty applies.
                                                <hr className="my-1" />
                                                <strong>Unchecked:</strong> penalties are calculated based on how far in the past the activity date is from today. Activities older than 5 days receive 0 points.
                                            </div>
                                        </Tooltip>
                                    }
                                >
                                    <i className="bi bi-info-circle admin-strava-help-icon"></i>
                                </OverlayTrigger>
                            </div>
                        </Form.Group>

                        <Button type="submit" variant="primary" disabled={!canSubmit}>
                            {syncing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Resyncing…
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-arrow-clockwise me-1"></i>
                                    Resync
                                </>
                            )}
                        </Button>
                    </Form>
                </div>
            </div>

            {result && (
                <Alert
                    variant={result.type === 'success' ? 'success' : 'danger'}
                    dismissible
                    onClose={() => setResult(null)}
                    className="admin-strava-result"
                >
                    {result.type === 'success' ? (
                        result.data.users ? (
                            <AllUsersResult data={result.data} />
                        ) : (
                            <SingleUserResult data={result.data} />
                        )
                    ) : (
                        <>
                            <Alert.Heading>Resync failed</Alert.Heading>
                            <p className="mb-0">{result.message}</p>
                        </>
                    )}
                </Alert>
            )}

            {hasViewAthletesPermission && (
                <div className="admin-strava-card admin-strava-quota-card">
                    <div className="admin-strava-card-header d-flex justify-content-between align-items-center">
                        <span>
                            <i className="bi bi-speedometer2 me-2"></i>
                            API Quota
                        </span>
                        <button
                            className="admin-strava-refresh-btn"
                            onClick={fetchQuota}
                            disabled={loadingQuota}
                            title="Refresh"
                        >
                            <i className={`bi bi-arrow-clockwise${loadingQuota ? ' spin' : ''}`}></i>
                        </button>
                    </div>
                    <div className="admin-strava-card-body">
                        {quotaError ? (
                            <div className="text-danger" style={{ fontSize: 'var(--font-size-sm)' }}>
                                <i className="bi bi-exclamation-circle me-1"></i>{quotaError}
                            </div>
                        ) : loadingQuota && !quota ? (
                            <div className="admin-strava-loading-users">
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Loading…
                            </div>
                        ) : quota && (
                            <>
                                <QuotaBar label="15-minute window" used={quota.used15min} limit={quota.limit15min} />
                                <QuotaBar label="Daily" used={quota.usedDaily} limit={quota.limitDaily} />
                            </>
                        )}
                    </div>
                </div>
            )}

            {hasViewAthletesPermission && (
                <div className="admin-strava-card admin-strava-athletes-card">
                    <div className="admin-strava-card-header d-flex justify-content-between align-items-center">
                        <span>
                            <i className="bi bi-person-lines-fill me-2"></i>
                            Connected Athletes
                        </span>
                        <button
                            className="admin-strava-refresh-btn"
                            onClick={fetchAthletes}
                            disabled={loadingAthletes}
                            title="Refresh"
                        >
                            <i className={`bi bi-arrow-clockwise${loadingAthletes ? ' spin' : ''}`}></i>
                        </button>
                    </div>
                    <div className="admin-strava-card-body p-0">
                        {loadingAthletes ? (
                            <div className="admin-strava-loading-users p-3">
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Loading athletes…
                            </div>
                        ) : athletesError ? (
                            <div className="p-3 text-danger" style={{ fontSize: 'var(--font-size-sm)' }}>
                                <i className="bi bi-exclamation-circle me-1"></i>{athletesError}
                            </div>
                        ) : athletes.length === 0 ? (
                            <div className="p-3 admin-strava-description mb-0">
                                No connected athletes found.
                            </div>
                        ) : (
                            <Table size="sm" className="admin-strava-users-table mb-0">
                                <thead>
                                    <tr>
                                        <th>FitOps user</th>
                                        <th>Strava name</th>
                                        <th>Strava ID</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {athletes.map(a => {
                                        const result = verifyResults[a.username];
                                        return (
                                            <React.Fragment key={a.username}>
                                                <tr>
                                                    <td>{a.username}</td>
                                                    <td>{[a.athleteFirstName, a.athleteLastName].filter(Boolean).join(' ') || <span className="text-muted">—</span>}</td>
                                                    <td>
                                                        <a
                                                            href={`https://www.strava.com/athletes/${a.stravaAthleteId}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="admin-strava-athlete-link"
                                                        >
                                                            {a.stravaAthleteId}
                                                            <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.7em' }}></i>
                                                        </a>
                                                    </td>
                                                    <td className="text-end">
                                                        <button
                                                            className="admin-strava-verify-btn"
                                                            onClick={() => handleVerify(a.username)}
                                                            disabled={verifying[a.username]}
                                                            title="Verify credentials against Strava"
                                                        >
                                                            {verifying[a.username]
                                                                ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                : <><i className="bi bi-patch-check me-1"></i>Verify</>
                                                            }
                                                        </button>
                                                    </td>
                                                </tr>
                                                {result && (
                                                    <tr className={`admin-strava-verify-row admin-strava-verify-row--${result.ok ? 'ok' : 'err'}`}>
                                                        <td colSpan={4}>
                                                            {result.ok ? (
                                                                <VerifyResult data={result.data} />
                                                            ) : (
                                                                <span className="text-danger">
                                                                    <i className="bi bi-exclamation-circle me-1"></i>{result.message}
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        )}
                    </div>
                </div>
            )}
        </Container>
    );
};

const SingleUserResult = ({ data }) => (
    <>
        <Alert.Heading>
            <i className="bi bi-check-circle me-2"></i>
            Resync complete for <strong>{data.username}</strong>
        </Alert.Heading>
        <ResultStats stats={data} />
    </>
);

const AllUsersResult = ({ data }) => (
    <>
        <Alert.Heading>
            <i className="bi bi-check-circle me-2"></i>
            Resync complete — {data.users.length} user{data.users.length !== 1 ? 's' : ''}
        </Alert.Heading>
        <Table size="sm" className="admin-strava-users-table mt-3 mb-0">
            <thead>
                <tr>
                    <th>User</th>
                    <th className="text-end">Found</th>
                    <th className="text-end">Imported</th>
                    <th className="text-end">Skipped</th>
                    <th className="text-end">Errors</th>
                </tr>
            </thead>
            <tbody>
                {data.users.map(u => (
                    <tr key={u.username}>
                        <td>{u.username}</td>
                        <td className="text-end">{u.total}</td>
                        <td className="text-end admin-strava-synced">{u.synced}</td>
                        <td className="text-end">{u.skipped}</td>
                        <td className={`text-end ${u.errors > 0 ? 'admin-strava-errors' : ''}`}>{u.errors}</td>
                    </tr>
                ))}
            </tbody>
            <tfoot>
                <tr className="admin-strava-totals-row">
                    <td><strong>Total</strong></td>
                    <td className="text-end"><strong>{data.totals.total}</strong></td>
                    <td className="text-end admin-strava-synced"><strong>{data.totals.synced}</strong></td>
                    <td className="text-end"><strong>{data.totals.skipped}</strong></td>
                    <td className={`text-end ${data.totals.errors > 0 ? 'admin-strava-errors' : ''}`}>
                        <strong>{data.totals.errors}</strong>
                    </td>
                </tr>
            </tfoot>
        </Table>
    </>
);

const ResultStats = ({ stats }) => (
    <div className="admin-strava-result-stats">
        <div className="admin-strava-stat">
            <span className="admin-strava-stat-value">{stats.total}</span>
            <span className="admin-strava-stat-label">Found on Strava</span>
        </div>
        <div className="admin-strava-stat admin-strava-stat-synced">
            <span className="admin-strava-stat-value">{stats.synced}</span>
            <span className="admin-strava-stat-label">Imported</span>
        </div>
        <div className="admin-strava-stat">
            <span className="admin-strava-stat-value">{stats.skipped}</span>
            <span className="admin-strava-stat-label">Skipped</span>
        </div>
        {stats.errors > 0 && (
            <div className="admin-strava-stat admin-strava-stat-errors">
                <span className="admin-strava-stat-value">{stats.errors}</span>
                <span className="admin-strava-stat-label">Errors</span>
            </div>
        )}
    </div>
);

const QuotaBar = ({ label, used, limit }) => {
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const variant = pct >= 90 ? 'danger' : pct >= 70 ? 'warning' : 'success';
    return (
        <div className="admin-strava-quota-row">
            <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="admin-strava-quota-label">{label}</span>
                <span className="admin-strava-quota-value">{used} / {limit}</span>
            </div>
            <div className="progress admin-strava-quota-progress">
                <div className={`progress-bar bg-${variant}`} style={{ width: `${pct}%` }} role="progressbar" />
            </div>
        </div>
    );
};

const VerifyResult = ({ data }) => (
    <div className="admin-strava-verify-result">
        {data.profileMedium && (
            <img src={data.profileMedium} alt="avatar" className="admin-strava-verify-avatar" />
        )}
        <div className="admin-strava-verify-info">
            <span className="admin-strava-verify-name">
                <i className="bi bi-check-circle-fill text-success me-1"></i>
                {[data.firstName, data.lastName].filter(Boolean).join(' ') || data.username || '(no name)'}
            </span>
            {(data.city || data.country) && (
                <span className="admin-strava-verify-location">
                    <i className="bi bi-geo-alt me-1"></i>
                    {[data.city, data.country].filter(Boolean).join(', ')}
                </span>
            )}
            {data.followerCount != null && (
                <span className="admin-strava-verify-meta">
                    {data.followerCount} followers · {data.friendCount} following
                </span>
            )}
        </div>
    </div>
);

export default AdminStrava;
